// Notificări (admin, manager): notificările trimise din aplicație — elevilor (mementouri, video,
// rezultatul examenului, documente) și personalului (de ex. „De sunat”). Sus rândul comun
// (.pageToolbar): perioada, căutarea destinatarului, cui (elevii, personalul sau un rol), tipul, starea. Dedesubt lista, cele mai
// noi întâi, cu „Afișează încă”. Lista o face serverul: GET /notifications/sent.
import React, { useCallback, useEffect, useRef, useState } from "react";

import { getSentNotifications } from "../../../api/notificationsService";
import { presetRange } from "../../../utils/auditLabels";
import { avatarColor, avatarInitials } from "../../../utils/userAvatar";
import { openPopup } from "../../Common/popupUIStore";
import { ButtonUI, CardUI, DropdownUI, ListUI, SearchBarUI, StatusUI, TagUI } from "../../UI";

const PAGE = 50;
const TYPING_DELAY_MS = 400;

const PERIODS = [
   { value: "today", label: "Azi" },
   { value: "yesterday", label: "Ieri" },
   { value: "7d", label: "Ultimele 7 zile" },
   { value: "30d", label: "Ultimele 30 de zile" },
   { value: "all", label: "Toată perioada" },
];

// cui: elevii, tot personalul sau un singur rol (ce primesc adminii, managerii, instructorii, profesorii)
const AUDIENCES = [
   { value: "", label: "Toți destinatarii" },
   { value: "students", label: "Elevii" },
   { value: "staff", label: "Tot personalul" },
   { value: "admin", label: "Administratorii" },
   { value: "manager", label: "Managerii" },
   { value: "instructor", label: "Instructorii" },
   { value: "professor", label: "Profesorii" },
];

export const NOTIFICATION_TYPES = {
   LESSON_REMINDER: "Memento lecție",
   LESSON_VIDEO: "Video",
   EXAM_RESULT: "Rezultat examen",
   EXAM_DOCUMENT: "Document",
   GENERAL: "General",
};

const TYPES = [{ value: "", label: "Toate tipurile" }, ...Object.entries(NOTIFICATION_TYPES).map(([value, label]) => ({ value, label }))];

const STATES = [
   { value: "", label: "Citite și necitite" },
   { value: "unread", label: "Necitite" },
   { value: "read", label: "Citite" },
];

const ROLE_SHORT = { ADMIN: "admin", MANAGER: "manager", INSTRUCTOR: "instructor", PROFESSOR: "profesor" };

const pad = (n) => String(n).padStart(2, "0");
function when(value) {
   if (!value) return "";
   const d = new Date(value);
   const today = new Date();
   const sameDay = d.toDateString() === today.toDateString();
   const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
   return sameDay ? `azi, ${time}` : `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}, ${time}`;
}

const fullName = (u) => `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.email || (u?.id ? `#${u.id}` : "—");

export default function SentNotificationsPage() {
   // implicit toată perioada: cele mai noi întâi, câte 50 („Afișează încă”)
   const [period, setPeriod] = useState("all");
   const [query, setQuery] = useState("");
   const [appliedQuery, setAppliedQuery] = useState("");
   // implicit: ce s-a trimis elevilor (personalul primește și el, de ex. „De sunat”)
   const [audience, setAudience] = useState("students");
   const [type, setType] = useState("");
   const [status, setStatus] = useState("");
   const [items, setItems] = useState([]);
   const [total, setTotal] = useState(0);
   const [unread, setUnread] = useState(0);
   const [page, setPage] = useState(1);
   const [totalPages, setTotalPages] = useState(0);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const requestRef = useRef(0);

   // căutarea se aplică după o pauză la scris
   useEffect(() => {
      const t = setTimeout(() => setAppliedQuery(query), TYPING_DELAY_MS);
      return () => clearTimeout(t);
   }, [query]);

   const load = useCallback(
      async (nextPage) => {
         const reqId = ++requestRef.current;
         setLoading(true);
         setError("");
         try {
            const range = period === "all" ? { from: null, to: null } : presetRange(period);
            const res = await getSentNotifications({
               q: appliedQuery,
               audience,
               type,
               status,
               from: range.from,
               to: range.to,
               page: nextPage,
               limit: PAGE,
            });
            if (reqId !== requestRef.current) return; // între timp alte filtre
            setItems((prev) => (nextPage > 1 ? [...prev, ...res.items] : res.items));
            setTotal(res.total);
            setUnread(res.unread);
            setPage(nextPage);
            setTotalPages(res.totalPages);
         } catch (e) {
            if (reqId === requestRef.current) setError(e?.message || "Nu am putut încărca notificările.");
         } finally {
            if (reqId === requestRef.current) setLoading(false);
         }
      },
      [period, appliedQuery, audience, type, status],
   );

   useEffect(() => {
      load(1);
   }, [load]);

   const anyFilter = Boolean(appliedQuery || type || status) || audience !== "students" || period !== "all";
   const resetFilters = () => {
      setPeriod("all");
      setQuery("");
      setAppliedQuery("");
      setAudience("students");
      setType("");
      setStatus("");
   };

   const openPerson = (u) => {
      if (!u?.id) return;
      if (u.role === "USER") openPopup("studentDetails", { student: { id: u.id, firstName: u.firstName, lastName: u.lastName, phone: u.phone } });
   };

   return (
      <div className="adminPage sentNotif">
         <div className="pageToolbar">
            <DropdownUI
               size="lg"
               surface="page"
               value={period}
               onChange={setPeriod}
               options={PERIODS}
               aria-label="Perioada"
               className="pageToolbar__menu"
            />
            <SearchBarUI
               surface="page"
               value={query}
               onChange={(e) => setQuery(e.target.value)}
               onClear={() => {
                  setQuery("");
                  setAppliedQuery("");
               }}
               placeholder="Caută destinatarul: nume, telefon, email sau id…"
               aria-label="Caută destinatarul"
               className="pageToolbar__search"
            />
            <div className="pageToolbar__controls">
               {/* în butoane, scurt (listele au textul întreg) */}
               <DropdownUI
                  size="lg"
                  surface="page"
                  value={audience}
                  label={audience === "staff" ? "Personalul" : AUDIENCES.find((a) => a.value === audience)?.label || "Toți"}
                  onChange={setAudience}
                  options={AUDIENCES}
                  aria-label="Cui"
               />
               <DropdownUI
                  size="lg"
                  surface="page"
                  value={type}
                  label={type ? NOTIFICATION_TYPES[type] : "Tipul"}
                  onChange={setType}
                  options={TYPES}
                  aria-label="Tipul"
               />
               <DropdownUI
                  size="lg"
                  surface="page"
                  align="end"
                  value={status}
                  label={status === "unread" ? "Necitite" : status === "read" ? "Citite" : "Starea"}
                  onChange={setStatus}
                  options={STATES}
                  aria-label="Starea"
               />
            </div>
         </div>

         <CardUI
            level="screen"
            titleSize="sm"
            title={`Notificări · ${total}`}
            actions={
               <TagUI.Group>
                  {loading ? <TagUI tone="muted">se încarcă…</TagUI> : null}
                  {unread ? (
                     <TagUI tone="accent" dot title="Necitite între cele găsite">
                        {unread} necitite
                     </TagUI>
                  ) : null}
                  {anyFilter ? (
                     <ButtonUI variant="ghost" size="sm" onClick={resetFilters}>
                        Resetează filtrele
                     </ButtonUI>
                  ) : null}
               </TagUI.Group>
            }
         >
            {error ? (
               <StatusUI tone="error" action={<ButtonUI size="sm" onClick={() => load(1)}>Reîncearcă</ButtonUI>}>
                  {error}
               </StatusUI>
            ) : null}
            <ListUI className="sentNotif__list">
               {items.map((n) => {
                  const u = n.user || {};
                  const student = u.role === "USER";
                  return (
                     <ListUI.Item
                        key={n.id}
                        className="sentNotif__item"
                        avatar={{ initials: avatarInitials(u), color: avatarColor(u), person: u }}
                        title={
                           <>
                              <span className="sentNotif__name">{fullName(u)}</span>
                              {u.role && u.role !== "USER" ? <TagUI tone="muted">{ROLE_SHORT[u.role] || u.role}</TagUI> : null}
                              {u.phone ? <span className="sentNotif__phone">{u.phone}</span> : null}
                           </>
                        }
                        meta={when(n.createdAt)}
                        detail={
                           <>
                              <span className="sentNotif__title">{n.title}</span>
                              <span className="sentNotif__body">{n.body}</span>
                           </>
                        }
                        aside={
                           <span className="sentNotif__aside">
                              <TagUI tone={n.type === "LESSON_REMINDER" ? "info" : n.type === "EXAM_RESULT" ? "success" : "neutral"}>
                                 {NOTIFICATION_TYPES[n.type] || n.type}
                              </TagUI>
                              <span className={`sentNotif__read${n.isRead ? " is-read" : ""}`}>
                                 {n.isRead ? `Citită${n.readAt ? ` ${when(n.readAt)}` : ""}` : "Necitită"}
                              </span>
                           </span>
                        }
                        // elevul: un clic deschide profilul lui
                        onClick={student ? () => openPerson(u) : undefined}
                     />
                  );
               })}
               {!loading && !items.length && !error ? (
                  <ListUI.Empty>{anyFilter ? "Nicio notificare pentru filtrele alese." : "Încă nu s-a trimis nicio notificare."}</ListUI.Empty>
               ) : null}
               {items.length ? (
                  <ListUI.More
                     shown={items.length}
                     total={total}
                     next={page < totalPages ? Math.min(PAGE, total - items.length) : 0}
                     loading={loading}
                     onMore={() => load(page + 1)}
                  />
               ) : null}
            </ListUI>
         </CardUI>
      </div>
   );
}
